import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

const NAV = [
  { label: "Platform", to: "/platforms", testid: "nav-platform" },
  { label: "Solutions", to: "/solutions", testid: "nav-solutions" },
  { label: "Services", to: "/solutions#services", testid: "nav-services" },
  { label: "Company", to: "/company", testid: "nav-company" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const isActive = (to) => {
    const path = to.split("#")[0];
    return location.pathname === path || (path !== "/" && location.pathname.startsWith(`${path}/`));
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "border-b border-[#E2E8F0] bg-white/85 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div
        className={`mx-auto flex w-full max-w-[1200px] items-center justify-between px-5 transition-all duration-300 sm:px-8 ${
          scrolled ? "h-16" : "h-[78px]"
        }`}
      >
        <Link to="/" data-testid="header-logo" aria-label="iJeeva home" className="flex items-center">
          <img
            src="/logo.png"
            alt="iJeeva"
            className={`w-auto transition-all duration-300 ${scrolled ? "h-8" : "h-9"}`}
          />
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-8 lg:flex">
          {NAV.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              data-testid={item.testid}
              className={`text-[14.5px] font-medium transition-colors duration-200 ${
                isActive(item.to) && !item.to.includes("#") ? "text-[#1D305B]" : "text-[#526074] hover:text-[#1D305B]"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-6 lg:flex">
          <Link
            to="/contact"
            data-testid="nav-contact"
            className="text-[14.5px] font-medium text-[#526074] transition-colors duration-200 hover:text-[#1D305B]"
          >
            Contact Us
          </Link>
          <Link
            to="/contact"
            data-testid="nav-talk-to-expert"
            className="btn-brand group inline-flex items-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-10px_rgba(37,99,235,0.55)]"
          >
            Talk to an Expert
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-[#E2E8F0] bg-white text-[#1D305B] lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-[#E2E8F0] bg-white lg:hidden">
          <nav aria-label="Mobile" className="mx-auto flex max-w-[1200px] flex-col px-5 py-4 sm:px-8">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`mobile-${item.testid}`}
                className="border-b border-[#F1F5F9] py-4 text-[17px] font-medium text-[#1D305B]"
              >
                {item.label}
              </Link>
            ))}
            <Link to="/contact" data-testid="mobile-nav-contact" className="py-4 text-[17px] font-medium text-[#1D305B]">
              Contact Us
            </Link>
            <Link
              to="/contact"
              data-testid="mobile-nav-talk-to-expert"
              className="group mb-4 mt-2 inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#1D305B] px-5 py-3.5 text-[15px] font-semibold text-white"
            >
              Talk to an Expert
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
